/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import Tabs from '@intelligence/components/NewIconTabs';

import SvgIcon from '@home/components/SvgIcon';
import TrendCard from '../TrendCard';
import './index.scss';
import EngagementCharts from './EngagementCharts';
import SentimentCharts from '@op/components/Dashboard/SentimentCharts';
import { getStudioCommunity } from '@op/services';
import { canRequest } from '../utils';
import { reviewsFormat, sentimentFormat } from '@op/utils/value';
import { useIntl } from 'umi';
import CardCommon from '@op/components/CardCommon';
import dataFormat from '@home/utils/dbDataFormat/dataFormat';
import { StudioContext } from '../../context';
import { hideSentimentChartStudio } from '../_config/config';

const communityTitle: any = {
  all: [
    { title: 'Mentions', value: 'mentions' },
    { title: 'Sentiment', value: 'avg_sentiment' },
  ],
  posts: [
    { title: 'Post Count', value: 'account_posts' },
    { title: 'Engagement', value: 'engagement' },
  ],
  video: [
    { title: 'Video Count', value: 'video_count' },
    { title: 'Video Views', value: 'video_views' },
  ],
  streaming: [
    { title: 'Hours Watched', value: 'sum_hours_watched' },
    { title: 'Avg. CCV', value: 'avg_ccv' },
  ],
};

const communityChartTitle: any = {
  all: 'Mentions',
  posts: 'Post Count',
  video: 'Video Views',
  streaming: 'Hours Watched',
};

interface IProps {
  // 筛选器
  filter: any;
  // 竞品浮窗是否延时请求
  isDelay?: boolean;
  tooltipMaxTop?: number;
  chartHeight?: number;
  config?: {
    // 竞品浮窗是否延时请求
    isDelay?: boolean;
    className: string;
    style: React.CSSProperties;
  };
};

export default function CommunityPerformance(props: IProps) {
  const { filter, isDelay, config, tooltipMaxTop, chartHeight } = props || {};
  const { currentGame } = useContext(StudioContext);
  const showSentiment = !hideSentimentChartStudio.includes(currentGame?.studio_code);
  const isDelayVal = isDelay ? isDelay : config?.isDelay;
  const { className, style } = config || {};

  const { formatMessage } = useIntl();
  const [loading, setLoading] = useState(false);
  const [empty, setEmpty] = useState(false);
  const [activeId, setActiveId] = useState('all');
  const dateType = filter?.date_type;
  const isRequset = !(activeId === 'streaming' && dateType !== 'daily');

  useEffect(() => {
    if (!isRequset) {
      setActiveId('all');
    }
  }, [isRequset]);

  const tabs = useMemo(() => {
    let tabsArr = [
      {
        id: 'all',
        label: (
          <div className="tab-flex">
            <SvgIcon type="studio-all" className="tab-icon" />{' '}
            {formatMessage({ id: 'All' })}
          </div>
        ),
        // className: 'MobileGames',
        component: '',
      },
      {
        id: 'posts',
        label: (
          <div className="tab-flex">
            <SvgIcon type="studio-posts" className="tab-icon" />{' '}
            {formatMessage({ id: 'Posts' })}
          </div>
        ),
        // className: 'PCGames',
        component: '',
      },
      {
        id: 'video',
        label: (
          <div className="tab-flex">
            <SvgIcon type="studio-video" className="tab-icon" />{' '}
            {formatMessage({ id: 'Videos' })}
          </div>
        ),
        // className: 'ConsoleGames',
        component: '',
      },
      {
        id: 'streaming',
        label: (
          <div className="tab-flex">
            <SvgIcon type="studio-streaming" className="tab-icon" />{' '}
            {formatMessage({ id: 'Streaming' })}
          </div>
        ),
        component: '',
      }
    ];

    if (dateType !== 'daily') {
      tabsArr = tabsArr?.filter(item => item.id !== 'streaming');
    }
    return tabsArr;
  }, [formatMessage, dateType]);
  const onActive = (tab: { id: any; }) => {
    if (tab.id === activeId) return;
    setLoading(true);
    setActiveId(tab.id);
  };
  const [timelineInfo, setTimelineInfo] = useState({});
  const [dataInfo, setDataInfo] = useState({});

  function listGroupByItem(list: Array<any> = [], groupKey: string = 'name') {
    const result: any = {};
    (list || []).forEach((item) => {
      const key = item[groupKey];
      if (!result[key]) {
        result[key] = [];
      }
      result[key].push(item);
    });

    return result;
  }

  function requestServerData() {
    setEmpty(false);
    setLoading(true);
    setTimelineInfo({});
    setDataInfo({});
    getStudioCommunity({ ...filter, community_type: activeId })
      .then((data: any) => {
        const {
          list,
          timeline,
          sentiment_timeline,
          timeline_of_end_time,
          timeline_of_start_time,
        } = data;
        const groupData = listGroupByItem(timeline, 'name');
        setEmpty(!timeline || timeline?.length === 0);
        setTimelineInfo({
          timeline_of_start_time,
          timeline_of_end_time,
          timeline: groupData,
          sentimentTimeline: sentiment_timeline,
        });
        setDataInfo(list);
      }).catch(() => { }).finally(() => {
        setLoading(false);
      });
  }

  useEffect(() => {
    // streaming的时候从daily切换到其他时间粒度，不需要再请求streaming接口
    if (canRequest(filter) && isRequset) {
      requestServerData();
    }
  }, [filter, activeId]);

  return (
    <div className={className} style={style}>
      <TrendCard
        className="dbh-trend-card--community"
        title={'Community Performance'}
        // 使用舆情组件的moduleType作为接口请求的拦截处理，当社区组件被隐藏时，不请求接口
        moduleType={isRequset ? "community" : ""}
        filter={filter}
        communityType={activeId}
        noJump={empty || loading}
        formatValue={(value: string | number) => dataFormat({ value, formatRule: 'bmk' })}
        isLeft
        isDelay={isDelayVal}
      >
        <div className="dbh-community-mentions">
          <Tabs
            ceiling
            tabs={tabs}
            animated={false}
            activeId={activeId}
            onActive={onActive}
          />
          <CardCommon
            contentCard
            loading={loading}
            loadingHeight={256}
            emptyHeight={256}
            empty={empty}
            hasEmptyDesc={false}
          >
            <div className="community-mentions-flex">
              <EngagementCharts
                data={timelineInfo}
                filter={filter}
                leftTitle={communityChartTitle[activeId]}
                communityType={activeId}
                tooltipMaxTop={tooltipMaxTop}
                showSentiment={showSentiment}
                height={chartHeight}
              ></EngagementCharts>
              <MentionsShowCard
                activeId={activeId}
                data={dataInfo}
                formatMessage={formatMessage}
                showSentiment={showSentiment}
              ></MentionsShowCard>
            </div>
          </CardCommon>
        </div>
      </TrendCard>
    </div>
  );
}

function MentionsShowCard({ formatMessage, data, activeId, showSentiment }: any) {
  const [{ title: title1, value: value1 }, { title: title2, value: value2 }] =
    communityTitle[activeId];

  function commonElement(title: any, valueKey: any, showDesc = true) {
    const value = data?.[valueKey] || 0;

    return (
      <div className="mentions-box">
        <div className="mentions-box-title">{formatMessage({ id: title })}</div>
        <div className="mentions-box-num">
          {reviewsFormat(value.toFixed(2))}
        </div>
        {showSentiment && showDesc && <div className="mentions-box-desc">-</div>}
      </div>
    );
  }

  function otherElement(title: any, valueKey: any) {
    const value = data?.[valueKey] || 0;

    if (title === 'Sentiment') {
      return (
        <div className="sentiment-box">
          <div className="mentions-box-title">
            {formatMessage({ id: title })}
          </div>
          <div className="mentions-box-chart">
            <SentimentCharts
              sentiment={Number(sentimentFormat(value, 0))}
            ></SentimentCharts>
          </div>
        </div>
      );
    }
    return commonElement(title, valueKey, false);
  }

  return (
    <div className="mentions-sentiment-box">
      {commonElement(title1, value1)}
      {showSentiment && otherElement(title2, value2)}
    </div>
  );
}
